/**
 * The v2 content sync driver (PLAN.md "Client engine", "`POST /sync/content`").
 *
 * One pass: every note in the engine with a change to send, a change in
 * flight, an unknown rev (0) or a pull that said it moved, becomes one item
 * of `POST /sync/content` (at most 100 per request); each result goes through
 * the engine (`@caliu/utils` noteContentSync) and is stored. Repeats while
 * something is left to send, a few rounds at most.
 *
 * Storage and the device's seq allocation are injected, so this runs in a
 * test over a fake HTTP client.
 */

import {
  identityOp,
  isIdentityOp,
  prepareSubmit,
  receiveApplied,
  receiveChanges,
  receiveSnapshot,
  type ContentRequest,
  type NoteSyncState,
  type RemoteChange,
  type TextOp,
} from "@caliu/utils";
import type { SyncHttpClient } from "../../types";
import { Sentry } from "../sentry";

export const MAX_CONTENT_BATCH = 100;
const MAX_ROUNDS = 5;

export type ContentResult =
  | { kind: "applied"; rev: number; at: string }
  | { kind: "stale"; changes: RemoteChange[] }
  | { kind: "changes"; changes: RemoteChange[] }
  | { kind: "snapshot"; rev: number; text: string; at: string; yourLastSeq: number; reset?: boolean }
  | { kind: "disabled" }
  | { kind: "forbidden" }
  | { kind: "not_found" }
  | { kind: "too_large" }
  /**
   * Additive: this account is a member of a note whose owner turned sharing
   * off (the kill switch). Access is paused, not lost: retried on a later pass.
   */
  | { kind: "paused" }
  /** Additive: that item failed on the server; retried on a later pass. */
  | { kind: "error" };

export interface ContentItem {
  noteId: string;
  rev: number;
  /** On a catch-up: so a snapshot names this client's last seq. */
  clientId?: string;
  change?: { baseHash: string; clientId: string; seq: number; ops: TextOp; title?: string };
}

export interface SyncContentResponse {
  results: Array<{ noteId: string; result: ContentResult }>;
}

/** What the driver needs from local storage. Every call is synchronous. */
export interface ContentSyncStore {
  /** Note ids that need a request: unsent text, a change in flight, rev 0, or a pending catch-up. */
  contentCandidates(): string[];
  /** The note's engine state, checked (null outside the engine). */
  loadState(noteId: string): NoteSyncState | null;
  /** Stores `next`; when its text differs from `prev`'s, also the note's text (and what derives from it). */
  saveState(noteId: string, prev: NoteSyncState, next: NoteSyncState): void;
  noteTitle(noteId: string): string | undefined;
  /** A pull said the note moved: catch it up on the next pass. */
  addCatchUp(noteIds: string[]): void;
  clearCatchUp(noteId: string): void;
  /** Local text with no common base: saved as a new private note "Copy of …". */
  createOrphanCopy(noteId: string, text: string): void;
  /** A note another account owns (shared with this one). Absent: none are. */
  isSharedWithMe?(noteId: string): boolean;
  /** This account's role on the note as last stored (from a pull or the live socket). */
  shareRole?(noteId: string): "owner" | "editor" | "viewer" | null;
  /** The person lost edit access with unsent text, now saved as a copy. */
  onUnsentSavedAsCopy?(noteId: string): void;
  /**
   * Makes the note's change in flight durable (a small record of its own, not
   * the whole database). Awaited before every send of it: a seq that left the
   * device is never reused, so the change that carries it must survive a
   * closed tab. A rejection means it is not sent.
   */
  recordInflight?(noteId: string, inflight: NonNullable<NoteSyncState["inflight"]>): Promise<void>;
}

/** This tab's client id and its never-reused seqs. */
export interface SyncDeviceSource {
  readonly clientId: string;
  allocateSeq(): Promise<number>;
  /** Settles once `clientId` is known to be this tab's alone (awaited before a pass sends). */
  ready?(): Promise<void>;
}

export interface ContentSyncDeps {
  http: SyncHttpClient;
  store: ContentSyncStore;
  device: SyncDeviceSource;
  /**
   * Caller rule 2: called right before remote ops are applied to a note, in
   * the same synchronous turn, so the open editor's typing goes through
   * `localEdit` first.
   */
  beforeRemote?: (noteId: string) => void;
}

/** Wants a brand-new change in flight (and so a fresh seq). */
function needsNewSeq(s: NoteSyncState): boolean {
  return s.inflight === null && s.rev !== 0 && s.base !== null && !isIdentityOp(s.buffer);
}

export class ContentSync {
  constructor(private deps: ContentSyncDeps) {}

  /** One pass. `disabled` means the server turned v2 off: the caller switches to legacy. */
  async run(): Promise<{ disabled: boolean }> {
    const { store, http, device } = this.deps;
    await device.ready?.();
    const skipped = new Set<string>();
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const ids = store.contentCandidates().filter((id) => !skipped.has(id)).slice(0, MAX_CONTENT_BATCH);
      if (ids.length === 0) return { disabled: false };

      const items: ContentItem[] = [];
      const sent = new Map<string, ContentRequest>();
      const inflight = new Map<string, NonNullable<NoteSyncState["inflight"]>>();
      for (const noteId of ids) {
        const first = store.loadState(noteId);
        if (!first) {
          store.clearCatchUp(noteId);
          continue;
        }
        // The seq comes first (it may wait on another tab); the state is read
        // again after, in the same turn as the write that puts it in flight.
        const seq = needsNewSeq(first) ? await device.allocateSeq() : 0;
        const s = store.loadState(noteId);
        if (!s) continue;
        if (needsNewSeq(s) && seq === 0) continue; // typed meanwhile: next round
        const r = prepareSubmit(s, { clientId: device.clientId, nextSeq: seq });
        // The change in flight is stored BEFORE it is sent: a seq is never reused.
        if (r.state !== s) store.saveState(noteId, s, r.state);
        if (r.state.inflight) inflight.set(noteId, r.state.inflight);
        const request: ContentRequest = r.request ?? { kind: "catchUp", rev: s.rev };
        sent.set(noteId, request);
        items.push(
          request.kind === "submit"
            ? {
                noteId,
                rev: request.baseRev,
                change: {
                  baseHash: request.baseHash,
                  clientId: request.clientId,
                  seq: request.seq,
                  ops: request.ops,
                  title: store.noteTitle(noteId),
                },
              }
            : { noteId, rev: request.rev, clientId: device.clientId },
        );
      }
      // On disk before it leaves; a change whose record could not be written stays here.
      if (store.recordInflight) {
        const record = store.recordInflight.bind(store);
        const failed = await Promise.all(
          items.map(async (item) => {
            const change = inflight.get(item.noteId);
            if (!item.change || !change) return null;
            try {
              await record(item.noteId, change);
              return null;
            } catch (err) {
              Sentry.captureException(err, { tags: { operation: "inflight_record" }, extra: { noteId: item.noteId } });
              return item.noteId;
            }
          }),
        );
        for (const noteId of failed) {
          if (noteId === null) continue;
          skipped.add(noteId);
          sent.delete(noteId);
        }
        items.splice(0, items.length, ...items.filter((i) => sent.has(i.noteId)));
      }
      if (items.length === 0) return { disabled: false };

      let response: SyncContentResponse;
      try {
        response = await http.fetchOrThrow<SyncContentResponse>("/sync/content", {
          method: "POST",
          body: JSON.stringify({ items }),
        });
      } catch (err) {
        // A worker without the route (rolled back, or an old deploy): like `disabled`.
        if (statusOf(err) === 404 || statusOf(err) === 405) return { disabled: true };
        throw err;
      }

      for (const { noteId, result } of response.results) {
        const request = sent.get(noteId);
        if (!request) continue;
        if (result.kind === "disabled") return { disabled: true };
        if (result.kind === "error" || result.kind === "paused") {
          // The server failed on this note alone, or its owner paused sharing
          // (the kill switch): unsent text stays, the next pass retries it.
          skipped.add(noteId);
          continue;
        }
        if (result.kind === "forbidden" && store.isSharedWithMe?.(noteId) && store.shareRole?.(noteId) !== "viewer") {
          // Forbidden while this device still thinks it may edit: the role
          // change has not reached it yet (or the answer was transient). Keep
          // the text and retry; the pull or the live socket brings the role.
          skipped.add(noteId);
          continue;
        }
        if ((result.kind === "forbidden" || result.kind === "not_found") && store.isSharedWithMe?.(noteId)) {
          // A note shared with this account that it may no longer edit (a
          // viewer now) or open (removed): what it had not sent is saved as
          // "Copy of …" and the note goes back to the server's text.
          this.keepUnsentAsCopy(noteId);
          store.clearCatchUp(noteId);
          skipped.add(noteId);
          continue;
        }
        if (result.kind === "forbidden" || result.kind === "not_found" || result.kind === "too_large") {
          // Not retried this pass. `not_found`: deleted elsewhere (the pull
          // removes it). `too_large`: the note stays local until it is shorter.
          // `forbidden` never happens to an owner.
          if (result.kind === "forbidden") {
            Sentry.captureException(new Error("sync content forbidden"), { extra: { noteId } });
          }
          if (result.kind === "not_found") {
            // Expected after a delete elsewhere, but not with work still unsent.
            const s = store.loadState(noteId);
            if (s && (s.inflight !== null || !isIdentityOp(s.buffer))) {
              Sentry.captureException(new Error("sync content not_found with unsent text"), { extra: { noteId } });
            }
          }
          store.clearCatchUp(noteId);
          skipped.add(noteId);
          continue;
        }
        try {
          this.applyResult(noteId, request, result, skipped);
        } catch (err) {
          // One note failing (a broken engine state, a storage error) never
          // stops the pass: it is reported and left for the next one.
          Sentry.captureException(err, { extra: { noteId, result: result.kind } });
          skipped.add(noteId);
        }
      }
    }
    return { disabled: false };
  }

  private keepUnsentAsCopy(noteId: string): void {
    keepUnsentAsCopy(this.deps.store, noteId, this.deps.beforeRemote);
  }

  /** Runs one result through the engine and stores it. */
  private applyResult(noteId: string, request: ContentRequest, result: ContentResult, skipped: Set<string>): void {
    const { store } = this.deps;
    this.deps.beforeRemote?.(noteId);
    const s = store.loadState(noteId);
    if (!s) return;
    let next = s;
    let needsCatchUp = false;
    if (result.kind === "applied") {
      if (request.kind === "submit") {
        const r = receiveApplied(s, { seq: request.seq, rev: result.rev, at: result.at });
        next = r.state;
        needsCatchUp = r.needsCatchUp;
      }
    } else if (result.kind === "stale" || result.kind === "changes") {
      const r = receiveChanges(s, result.changes);
      next = r.state;
      needsCatchUp = r.needsCatchUp;
    } else if (result.kind === "snapshot" && request.kind === "catchUp" && s.inflight !== null) {
      // A change went in flight after this catch-up left (the engine forbids
      // the snapshot then: it would rebase that change as unsent and send it
      // twice). Ask again: the next round resends the change itself.
      needsCatchUp = true;
    } else if (result.kind === "snapshot") {
      const r = receiveSnapshot(s, {
        rev: result.rev,
        text: result.text,
        at: result.at,
        yourLastSeq: result.yourLastSeq,
        reset: result.reset,
      });
      next = r.state;
      if (r.orphan !== undefined) store.createOrphanCopy(noteId, r.orphan);
    }
    if (next !== s) store.saveState(noteId, s, next);
    if (needsCatchUp) store.addCatchUp([noteId]);
    else store.clearCatchUp(noteId);
    // A catch-up that changed nothing is done; anything else is looked at again next round.
    if (request.kind === "catchUp" && next === s && !needsCatchUp) skipped.add(noteId);
  }
}

/**
 * Unsent text of a note this account lost edit access to (made a viewer, or
 * removed): saved as "Copy of …", then the note goes back to its base. True
 * when a copy was made. Shared by the HTTP driver and the live socket.
 */
export function keepUnsentAsCopy(
  store: Pick<ContentSyncStore, "loadState" | "saveState" | "createOrphanCopy" | "onUnsentSavedAsCopy">,
  noteId: string,
  beforeRemote?: (noteId: string) => void,
): boolean {
  beforeRemote?.(noteId);
  const s = store.loadState(noteId);
  if (!s || s.base === null || (s.inflight === null && isIdentityOp(s.buffer))) return false;
  store.createOrphanCopy(noteId, s.content);
  store.saveState(noteId, s, dropUnsent(s));
  store.onUnsentSavedAsCopy?.(noteId);
  return true;
}

/** The state with nothing unsent: the local text is the base again. */
export function dropUnsent(s: NoteSyncState): NoteSyncState {
  if (s.base === null) return s;
  return { ...s, content: s.base, inflight: null, buffer: identityOp(s.base.length) };
}

function statusOf(err: unknown): number | undefined {
  const status = typeof err === "object" && err !== null ? (err as { status?: unknown }).status : undefined;
  return typeof status === "number" ? status : undefined;
}
